// Third-party
import Head from 'next/head';

// First-party
import { Slot, createPage } from '@lib/util/templateHelpers';
import { gameToLdJson } from '@lib/util/gameToLdJson';
import Meta from '@lib/components/head/Meta';

// Component lib
import shared from '@lib/components';

// Local
import gameApi from '@/apis/drupal/game';
import GameTileLayout from '@/layouts/GameTileLayout';

// Local components
import AllGamesBlock from '@/components/AllGamesBlock';

const {
	Box, Block, Title, RouterLink, DocumentTitle, Content
} = shared;

export async function getServerSideProps () {
  const games = await gameApi.listPopularAlt(process.env.APP_CONFIG.DEFAULT_GAME_LIMIT);
  
  return {
    props: {
      games
    },
  };
}

export default createPage('PopularPage', {}, function PopularPage ({ className }, props) {
  const breadcrumbs = [
    { url: `${process.env.APP_CONFIG.URL}/popular`, name: 'Popular io Games' }
  ];

  const title = 'Popular IO Games';
  const description = 'Discover the most-played free online games right now on iogames.space. From Smash Karts to Powerline.io, these games represent the best that browser gaming has to offer.';

  return (
    <GameTileLayout className={className}>
      <DocumentTitle title={title} />

      <Meta
		title={title}
		description={description}
		openGraph={{
		title: title,
		description: description,
		type: 'website'
		}}
		>
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'WebSite',
              'name': process.env.APP_CONFIG.DEFAULT_TITLE,
              'url': process.env.APP_CONFIG.URL + '/popular'
            }, null, 2)
          }}
        />
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "Thing", 
                    "@id": process.env.APP_CONFIG.URL,
                    "name": "Games"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "Thing", 
                    "@id":process.env.APP_CONFIG.URL + '/popular',
                    "name": "Popular io Games"
                  }
                }
              ],
            }, null, 2)
          }}
        />

        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@type': 'ItemList',
              'itemListElement': props.games.map((game, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'url': process.env.APP_CONFIG.URL + game.path,
                'name': game.name
              //  'item': gameToLdJson(game)
              }))
            }, null, 2)
          }}
        />
      </Meta>

      <AllGamesBlock feature games={props.games} fetchMoreGames={gameApi.listPopularAlt}>
        <Slot name='title'>Popular io Games</Slot>
      </AllGamesBlock>
      {
        <Block>
          <Box className='IndexPage__BottomBox'>
            <Title>Popular IO Games</Title>
            <Content>
              <Title h2 size4>The Most-Played Free Online Games Right Now</Title>

              <p>Welcome to our Popular Games collection, where you'll find the cream of the crop in io gaming! These titles have captured the attention of millions of players worldwide for good reason. From the adrenaline-pumping action of <RouterLink href="/smash-karts-io-game">Smash Karts</RouterLink> to the strategic mayhem of <RouterLink href="/powerline-io">Powerline.io</RouterLink>, these games represent the best that browser gaming has to offer.</p>
             
              <Title h2 size4>What Makes These Games Stand Out?</Title>
              <p>Popular io games earn their status through exceptional gameplay, innovative mechanics, and that special "just one more round" quality that keeps players coming back. The games featured in this section have:</p>
              <ul>
                <li>Proven appeal across diverse player demographics</li>
                <li>Refined gameplay mechanics that withstand the test of time</li>
                <li>Active communities of dedicated players</li>
                <li>Regular updates that keep the experience fresh</li>
                <li>The perfect balance of accessibility and depth</li>
              </ul>

              <Title h2 size4>Community Favorites</Title>
              <p>Currently trending titles include <RouterLink href="/shell-shockers">Shell Shockers</RouterLink>, the egg-based FPS that's scrambling the competition, and <RouterLink href="/basketbros-io">Basket Bros</RouterLink>, which brings fast-paced basketball action to your browser in exciting multiplayer matches. <RouterLink href="/deadshot-io">Deadshot</RouterLink> offers precision-based combat that tests your aim and reflexes, while <RouterLink href="/starblast-io">Starblast</RouterLink> lets you pilot spaceships in an interstellar battle arena with upgrade paths and team-based gameplay.</p>
              <p>These games have maintained their popularity through constant updates, balanced gameplay, and the perfect blend of casual accessibility with competitive depth.</p>              
             
              <Title h2 size4>How We Rank Popularity</Title>
              <p>Our popularity rankings are based on actual play data from the past week, ensuring you always see what's genuinely trending right now. These aren't subjective ratings—they represent real player choices across our platform.</p>             
              <p>The best way to understand why these games are so popular is to experience them yourself! No downloads or accounts needed—just click and play instantly. Challenge yourself against thousands of other players in these battle-tested favorites.</p>
              <p>Whether you're looking for intense competition or casual fun, our collection of popular io games has something for everyone. Dive in now and see why millions of players can't get enough of these addictive free online games!</p>
            </Content>
          </Box>
        </Block>
      }

    </GameTileLayout>
  );
});
